import React from 'react';

import { BodyBoldBB1, BodyRegularBR2 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { ArticlePriceProps } from './ArticlePrice';

interface LegalDrawerContentProps extends Pick<ArticlePriceProps, 'regularPrice'> {
  hasValuePackLabel: boolean;
  specialPrice?: Partial<ArticlePriceProps['specialPrice']>;
  coupon?: Pick<NonNullable<ArticlePriceProps['coupon']>, 'priceAfterDiscount'>;
  discountedPrice?: number | null;
}

const LegalDrawerContent = ({
  hasValuePackLabel,
  coupon,
  specialPrice,
  regularPrice,
  discountedPrice,
}: LegalDrawerContentProps) => {
  const { t, fc } = useLocalization();

  const textColor = 'sys.neutral.text.default';
  const renderPriceInfoSection = (subHeading: string, content: string) => (
    <>
      <BodyBoldBB1 color={textColor} p="sp_16" pb="sp_6">
        {subHeading}
      </BodyBoldBB1>
      <BodyRegularBR2 color={textColor} p="sp_16" pt="sp_0">
        {content}
      </BodyRegularBR2>
    </>
  );

  if (coupon) {
    return {
      title: t('h24_article_strike_price_disclaimer_title'),
      content: (
        <>
          {renderPriceInfoSection(
            fc(coupon.priceAfterDiscount),
            t('h24_article_price_after_coupon')
          )}
          {(discountedPrice !== undefined ? discountedPrice : specialPrice?.value) &&
            renderPriceInfoSection(
              fc(discountedPrice ?? specialPrice.value),
              t('h24_article_price_after_reduction')
            )}
          {renderPriceInfoSection(fc(regularPrice.value), t('h24_special_price_explanatory_text'))}
        </>
      ),
    };
  }

  if (hasValuePackLabel) {
    return {
      title: t('h24_pdp_discount_set_disclaimer_title'),
      content: renderPriceInfoSection(
        t('h24_set_price_drawer_sub_heading'),
        t('h24_value_pack_info')
      ),
    };
  }

  return {
    title: t('h24_article_strike_price_disclaimer_title'),
    content: renderPriceInfoSection(
      t('h24_strike_price_drawer_sub_heading'),
      t('h24_special_price_explanatory_text')
    ),
  };
};

export default LegalDrawerContent;
